import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import KPICard from './components/KPICard';
import ResourceConsumptionChart from './components/ResourceConsumptionChart';
import UserEngagementHeatmap from './components/UserEngagementHeatmap';
import PerformanceMonitoringTable from './components/PerformanceMonitoringTable';
import GlobalControls from './components/GlobalControls';

import Button from '../../components/ui/Button';

const UsageAnalyticsPerformanceMonitoring = () => {
  const [timeGranularity, setTimeGranularity] = useState('daily');
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [resourceTypes, setResourceTypes] = useState({
    storage: true,
    apiCalls: true,
    bandwidth: true,
    compute: true
  });
  const [selectedMetricCard, setSelectedMetricCard] = useState(null);

  // Mock KPI data
  const kpiData = [
    {
      title: "Active Users",
      value: "24,847",
      unit: "",
      change: "+12.5%",
      changeType: "positive",
      icon: "Users",
      threshold: "30,000",
      status: "good"
    },
    {
      title: "Storage Consumption",
      value: "2.4",
      unit: "TB",
      change: "+8.2%",
      changeType: "positive",
      icon: "HardDrive",
      threshold: "5TB",
      status: "good"
    },
    {
      title: "API Calls",
      value: "1.2M",
      unit: "/day",
      change: "+15.3%",
      changeType: "positive",
      icon: "Zap",
      threshold: "2M",
      status: "good"
    },
    {
      title: "Transaction Volume",
      value: "89,432",
      unit: "",
      change: "+5.7%",
      changeType: "positive",
      icon: "Activity",
      threshold: "100K",
      status: "warning"
    },
    {
      title: "Avg Session Duration",
      value: "24.5",
      unit: "min",
      change: "-2.1%",
      changeType: "negative",
      icon: "Clock",
      threshold: "30min",
      status: "warning"
    },
    {
      title: "Feature Adoption Rate",
      value: "78.9",
      unit: "%",
      change: "+3.4%",
      changeType: "positive",
      icon: "TrendingUp",
      threshold: "85%",
      status: "good"
    }
  ];

  // Mock chart data
  const chartData = Array.from({ length: 24 }, (_, i) => ({
    time: `${i}:00`,
    storage: Math.floor(Math.random() * 100) + 50,
    apiCalls: Math.floor(Math.random() * 80) + 30,
    bandwidth: Math.floor(Math.random() * 60) + 20,
    compute: Math.floor(Math.random() * 90) + 40
  }));

  // Mock heatmap data
  const heatmapData = Array.from({ length: 7 }, (_, dayIndex) =>
    Array.from({ length: 24 }, (_, hourIndex) => ({
      usage: Math.floor(Math.random() * 100),
      sessions: Math.floor(Math.random() * 50),
      duration: Math.floor(Math.random() * 120)
    }))
  );

  // Mock performance table data
  const performanceData = [
    {
      id: 1,
      name: "TechCorp Solutions",
      tier: "enterprise",
      users: 1247,
      storage: { used: 450, quota: 500 },
      apiCalls: { used: 85, quota: 100 },
      bandwidth: { used: 120, quota: 150 },
      efficiency: 92,
      recommendations: [
        { type: "optimize", message: "Consider API caching" },
        { type: "upgrade", message: "Storage upgrade available" }
      ]
    },
    {
      id: 2,
      name: "InnovateLabs Inc",
      tier: "professional",
      users: 543,
      storage: { used: 180, quota: 200 },
      apiCalls: { used: 45, quota: 50 },
      bandwidth: { used: 65, quota: 75 },
      efficiency: 87,
      recommendations: [
        { type: "optimize", message: "Optimize database queries" }
      ]
    },
    {
      id: 3,
      name: "StartupHub",
      tier: "starter",
      users: 89,
      storage: { used: 45, quota: 50 },
      apiCalls: { used: 18, quota: 20 },
      bandwidth: { used: 22, quota: 25 },
      efficiency: 94,
      recommendations: [
        { type: "upgrade", message: "Ready for Professional tier" }
      ]
    },
    {
      id: 4,
      name: "GlobalTech Enterprise",
      tier: "enterprise",
      users: 2156,
      storage: { used: 890, quota: 1000 },
      apiCalls: { used: 180, quota: 200 },
      bandwidth: { used: 245, quota: 300 },
      efficiency: 76,
      recommendations: [
        { type: "warning", message: "High storage usage" },
        { type: "optimize", message: "Review API efficiency" }
      ]
    },
    {
      id: 5,
      name: "CreativeStudio Pro",
      tier: "professional",
      users: 324,
      storage: { used: 165, quota: 200 },
      apiCalls: { used: 38, quota: 50 },
      bandwidth: { used: 58, quota: 75 },
      efficiency: 89,
      recommendations: [
        { type: "optimize", message: "Media optimization available" }
      ]
    }
  ];

  const handleTimeGranularityChange = (granularity) => {
    setTimeGranularity(granularity);
    console.log('Time granularity changed:', granularity);
  };

  const handleTenantFilterChange = (tenants) => {
    setSelectedTenants(tenants);
    console.log('Tenant filter changed:', tenants);
  };

  const handleResourceToggle = (resources) => {
    setResourceTypes(resources);
    console.log('Resource types changed:', resources);
  };

  const handleRefresh = () => {
    console.log('Data refreshed at:', new Date().toLocaleTimeString());
  };

  const handleChartZoom = (zoomLevel) => {
    console.log('Chart zoom changed:', zoomLevel);
  };

  const handleChartSeriesToggle = (visibleSeries) => {
    console.log('Chart series toggled:', visibleSeries);
  };

  const handleHeatmapCellClick = (day, hour, data) => {
    console.log('Heatmap cell clicked:', { day, hour, data });
  };

  const handleTableRowClick = (tenant) => {
    console.log('Table row clicked:', tenant);
  };

  const handleOptimizeTenant = (tenant) => {
    console.log('Optimize tenant:', tenant);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar />
      
      <main className="lg:ml-60 pt-16 lg:pt-0">
        <div className="p-6 space-y-6">
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div>
              <h1 className="text-2xl font-bold text-text-primary">Usage Analytics & Performance Monitoring</h1>
              <p className="text-text-secondary mt-1">
                Deep insights into platform utilization, user engagement patterns, and system performance
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button variant="outline" size="sm" iconName="Download">
                Export Report
              </Button>
              <Button variant="outline" size="sm" iconName="Settings">
                Configure Alerts
              </Button>
              <Button variant="default" size="sm" iconName="BarChart3">
                Advanced Analytics
              </Button>
            </div>
          </div>

          {/* Global Controls */}
          <GlobalControls
            onTimeGranularityChange={handleTimeGranularityChange}
            onTenantFilterChange={handleTenantFilterChange}
            onResourceToggle={handleResourceToggle}
            onRefresh={handleRefresh}
          />

          {/* KPI Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
            {kpiData.map((kpi, index) => (
              <KPICard
                key={index}
                title={kpi.title}
                value={kpi.value}
                unit={kpi.unit}
                change={kpi.change}
                changeType={kpi.changeType}
                icon={kpi.icon}
                threshold={kpi.threshold}
                status={kpi.status}
              />
            ))}
          </div>

          {/* Main Analytics Area */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Resource Consumption Chart - 2/3 width on desktop */}
            <div className="xl:col-span-2">
              <ResourceConsumptionChart
                data={chartData}
                onZoom={handleChartZoom}
                onToggleSeries={handleChartSeriesToggle}
              />
            </div>

            {/* User Engagement Heatmap - 1/3 width on desktop */}
            <div className="xl:col-span-1">
              <UserEngagementHeatmap
                data={heatmapData}
                onCellClick={handleHeatmapCellClick}
              />
            </div>
          </div>

          {/* Performance Monitoring Table */}
          <PerformanceMonitoringTable
            data={performanceData}
            onRowClick={handleTableRowClick}
            onOptimize={handleOptimizeTenant}
          />

          {/* Quick Actions Panel */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-text-primary">Quick Actions</h3>
              <Button variant="ghost" size="sm" iconName="MoreHorizontal" />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Button
                variant="outline"
                className="flex flex-col items-center space-y-2 h-20"
                iconName="AlertTriangle"
              >
                <span className="text-sm font-medium">View Alerts</span>
                <span className="text-xs text-text-secondary">3 active</span>
              </Button>
              
              <Button
                variant="outline"
                className="flex flex-col items-center space-y-2 h-20"
                iconName="TrendingUp"
              >
                <span className="text-sm font-medium">Usage Trends</span>
                <span className="text-xs text-text-secondary">Last 30 days</span>
              </Button>
              
              <Button
                variant="outline"
                className="flex flex-col items-center space-y-2 h-20"
                iconName="Users"
              >
                <span className="text-sm font-medium">User Activity</span>
                <span className="text-xs text-text-secondary">Real-time</span>
              </Button>
              
              <Button
                variant="outline"
                className="flex flex-col items-center space-y-2 h-20"
                iconName="Settings"
              >
                <span className="text-sm font-medium">Optimize</span>
                <span className="text-xs text-text-secondary">Performance</span>
              </Button>
            </div>
          </div>
        </div>
      </main>

      {/* Mobile Bottom Navigation Spacer */}
      <div className="h-20 lg:hidden" />
    </div>
  );
};

export default UsageAnalyticsPerformanceMonitoring;